import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { withStyles } from '@material-ui/styles';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import CardHeader from '@material-ui/core/CardHeader';
import IconButton from '@material-ui/core/IconButton';
import Done from '@material-ui/icons/Done';
import Icon from '@material-ui/core/Icon';
import MoreHorizIcon from '@material-ui/icons/MoreHoriz';
import { uiState } from 'react-redux-ui-state';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import MenuItem from '@material-ui/core/MenuItem';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import Button from '@material-ui/core/Button';
import { reduxForm, Field, SubmissionError } from 'redux-form';
import { required, maxLength, email } from 'services/validationMsgs';
import WrappedTextField from 'components/WrappedTextField/WrappedTextField';
import MorePropTypes from 'airbnb-prop-types';
import Tooltip from '@material-ui/core/Tooltip';

import BusyButton from 'components/BusyButton/BusyButton';
import ORTypography from 'components/ORTypography/ORTypography';
import UserActionsMenu from './UserActionsMenu/UserActionsMenuContainer';
import { RolesForInvites } from '../../../../constants/roles';

const msg = 'Contact Support to make changes';

const maxLength128 = maxLength(128);

const propTypes = {
  classes: PropTypes.shape({}).isRequired,
  className: PropTypes.string,
  updateUser: PropTypes.func.isRequired,
  deleteUser: PropTypes.func.isRequired,
  getUsers: PropTypes.func.isRequired,
  setUiState: PropTypes.func.isRequired,
  confirmDeleteOpen: PropTypes.bool.isRequired,
  menuAnchorEl: PropTypes.shape({}),
  handleSubmit: PropTypes.func.isRequired,
  actingUserId: PropTypes.string.isRequired,
  user: PropTypes.shape({
    id: PropTypes.string.isRequired,
    email: PropTypes.string.isRequired,
    roles: PropTypes.arrayOf(PropTypes.string).isRequired,
    username: PropTypes.string.isRequired,
    ssoUsername: MorePropTypes.or([MorePropTypes.explicitNull, PropTypes.string]),
    orv4id: PropTypes.string,
  }).isRequired,
  pristine: PropTypes.bool.isRequired,

  valid: PropTypes.bool.isRequired,
  submitting: PropTypes.bool.isRequired,
  error: MorePropTypes.or([MorePropTypes.explicitNull, PropTypes.string, PropTypes.shape({})]),
};

const defaultProps = {
  error: null,
  menuAnchorEl: null,
  className: null,
};

export function RenderCheckbox({ input, label }) {
  return (
    <Tooltip title={msg}>
      <FormControlLabel
        control={<Checkbox checked={input.value} onChange={input.onChange} disabled />}
        label={label}
      />
    </Tooltip>
  );
}

RenderCheckbox.propTypes = {
  input: PropTypes.shape({
    value: PropTypes.bool.isRequired,
    onChange: PropTypes.func.isRequired,
  }).isRequired,
  label: PropTypes.string.isRequired,
};

export function UnstyledUserCardForm({
  updateUser,
  deleteUser,
  getUsers,
  classes,
  className,
  actingUserId,
  user,
  setUiState,
  menuAnchorEl,
  handleSubmit,
  pristine,
  valid,
  error,
  submitting,
  confirmDeleteOpen,
}) {
  const handleFormSubmissionReturn = (result) => {
    if (result.error) {
      throw new SubmissionError(result.formErrors);
    }
    getUsers();
  };

  const openMenu = (event) => {
    setUiState({ menuAnchorEl: event.currentTarget });
  };

  const closeMenu = () => {
    setUiState({ menuAnchorEl: null });
  };

  const handleConfirmDelete = () => {
    setUiState({
      confirmDeleteOpen: !confirmDeleteOpen,
      menuAnchorEl: null,
    });
  };

  const handleDelete = (userId) => {
    setUiState({
      confirmDeleteOpen: false,
    });
    deleteUser(userId);
  };

  const handleDialogCancel = () => {
    setUiState({
      confirmDeleteOpen: false,
    });
  };

  const submitHandler = (data) => updateUser(data, user.id).then(handleFormSubmissionReturn);

  const errorElm = error ? (
    <ORTypography color="error" className={classes.formStatus}>
      {error}
    </ORTypography>
  ) : null;

  let tempRole = user.roles[0];
  if (tempRole === 'readonly user') {
    tempRole = 'Read Only';
  }

  return (
    <React.Fragment>
      <Card className={classnames(className, classes.cardContainer)}>
        <CardHeader
          avatar={<Icon className={classes.icon}>account_circle</Icon>}
          action={
            user.username !== actingUserId && (
              <IconButton
                aria-label="More User Actions"
                onClick={openMenu}
                classes={{ root: classes.moreIconRoot }}
              >
                <MoreHorizIcon />
              </IconButton>
            )
          }
          title={user.ssoUsername ? user.ssoUsername : user.email}
          subheader={tempRole}
          classes={{
            action: classes.action,
            subheader: classes.capitalize,
            title: classes.title,
            content: classes.content,
          }}
        />
        <CardContent>
          <div className={classes.moreContainer}>
            <UserActionsMenu
              handleConfirmDelete={handleConfirmDelete}
              anchorEl={menuAnchorEl}
              onClose={closeMenu}
              isOwner={user.username === actingUserId}
              userId={user.id}
              userORv4ID={user.orv4id}
            />
            <Dialog open={confirmDeleteOpen}>
              <DialogContent classes={{ root: classes.dialogText }}>
                Are you sure you want to delete&nbsp;
                {user.email}?
              </DialogContent>
              <DialogActions>
                <Button
                  color="secondary"
                  onClick={handleDialogCancel}
                  className={classes.dialogButton}
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => handleDelete(user.id)}
                  className={classes.confirmDeleteButton}
                >
                  Confirm
                </Button>
              </DialogActions>
            </Dialog>
          </div>
          <form onSubmit={handleSubmit(submitHandler)}>
            <div>
              <Field
                className={classes.emailInput}
                variant="outlined"
                name="email"
                label="Email"
                placeholder="Email"
                component={WrappedTextField}
                fullWidth
                disabled
                validate={[maxLength128, required, email]}
                margin="normal"
                InputLabelProps={{
                  shrink: true,
                }}
              >
                {user.email}
              </Field>
              <Field
                variant="outlined"
                select
                fullWidth
                name="role"
                label="Role"
                component={WrappedTextField}
                placeholder="Select a Role"
                defaultValue={user.roles[0]}
                inputProps={{
                  InputLabelProps: {
                    shrink: true,
                    className: classes.inputLabel,
                    defaultValue: 'readonly user',
                  },
                }}
                margin="normal"
              >
                {RolesForInvites.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Field>
              <div className={classes.checkBoxContainer}>
                <Field
                  variant="contained"
                  name="resetFlag"
                  component={RenderCheckbox}
                  label="Password Reset"
                  type="checkbox"
                />
              </div>
            </div>
            {errorElm}
            <div className={classes.addButtonContainer}>
              <BusyButton
                disabled={!valid || pristine}
                loading={submitting}
                name="submit"
                type="submit"
                variant="fab"
                color="primary"
                size="small"
                className={classes.addButton}
              >
                <Done fontSize="large" />
              </BusyButton>
            </div>
          </form>
        </CardContent>
      </Card>
    </React.Fragment>
  );
}

UnstyledUserCardForm.propTypes = propTypes;
UnstyledUserCardForm.defaultProps = defaultProps;

const styles = (theme) => ({
  moreContainer: {
    display: 'flex',
    width: '100%',
    justifyContent: 'flex-end',
  },
  moreIconRoot: {
    padding: 0,
    marginRight: theme.spacing(1),
  },
  cardContainer: {
    marginTop: theme.spacing(1),
  },
  action: {
    alignSelf: 'flex-end',
  },
  capitalize: {
    textTransform: 'capitalize',
  },
  expand: {
    transform: 'rotate(0deg)',
    marginLeft: 'auto',
    transition: theme.transitions.create('transform', {
      duration: theme.transitions.duration.shortest,
    }),
  },
  expandOpen: {
    transform: 'rotate(180deg)',
  },
  addButtonContainer: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
  },
  addButton: {
    cursor: 'pointer',
    margin: theme.spacing(2),
  },
  cancelButton: {
    cursor: 'pointer',
    margin: theme.spacing(2),
    color: theme.customColors.white,
    backgroundColor: theme.customColors.teal,
    '&:hover': {
      background: theme.customColors.teal,
    },
  },
  confirmDeleteButton: {
    color: theme.customColors.red,
  },
  checkBoxContainer: {
    display: 'flex',
    flexDirection: 'column',
  },
  popover: {
    zIndex: 2000,
  },
  emailInput: {
    marginTop: theme.spacing(1),
  },
  icon: {
    fontSize: 50,
    color: 'teal',
    [theme.breakpoints.down('sm')]: {
      fontSize: 35,
    },
  },
  title: {
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    [theme.breakpoints.down('sm')]: {
      fontSize: '14px',
    },
  },
  content: {
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
  },
});

export const uiConfig = {
  state: () => ({
    userCardFormOpen: false,
    menuAnchorEl: null,
    confirmDeleteOpen: false,
  }),
};

export default uiState(uiConfig)(
  withStyles(styles)(
    reduxForm({
      enableReinitialize: true,
    })(UnstyledUserCardForm),
  ),
);
